import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header';
import databg from '@/assets/products/wt.png';

import { Globe, Smartphone, TrendingUp, Search, Activity, BarChart3 } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/web-traffic');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/traffic-funnel-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/traffic-momentum-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/consumer-attention-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/demand-pulse-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const WebTrafficPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Web Traffic Data API: Consumer Engagement"
        description="Web traffic data API with website visitor analytics, app downloads, and digital presence data for 5,000+ companies. Traffic analytics API for consumer interest data."
        canonicalUrl="https://axionquant.com/products/webtraffic"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Web Traffic Data API",
          "description": "Web traffic data API with website visitor analytics, app downloads, and digital presence data for 5,000+ companies. Traffic analytics API for consumer interest data.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Web Traffic Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
  docs="/docs/02-alternative-data/web-traffic"
  title="Web Traffic Data API – The Digital Pulse of Business"
  body="Website traffic data API that quantifies online demand before it shows up in earnings. Track monthly visitors, engagement quality, and consumer interest data across any set of consumer-facing companies."
  crumb={{name: 'Web Traffic', path:'/products/web-traffic'}}
  tabs={[
    {
      label: 'Traffic Benchmarking',
      language: 'python',
      filename: 'traffic_benchmarking.py',
      bg: '10.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["AMZN", "SHOP", "EBAY", "ETSY", "WMT", "TGT"]

def fetch_traffic(row):
    t       = row["ticker"]
    traffic = client.web_traffic.traffic(t)
    return (t, {
        "ticker":   t,
        "visitors": traffic["monthlyVisitors"],
        "rank":     traffic["globalRank"],
        "bounce":   traffic["bounceRate"],
    })

df_tickers  = utils.df([{"ticker": t} for t in tickers])
traffic_map = {}
utils.work(df_tickers, fetch_traffic, traffic_map)

df_traffic = utils.df(list(traffic_map.values()))
df_traffic = df_traffic.sort_values("visitors", ascending=False)

visualize.barh(df_traffic, x="visitors", y="ticker")
`,
    },
    {
      label: 'Engagement Quality',
      language: 'python',
      filename: 'engagement_quality.py',
      bg: '8.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["NFLX", "DIS", "PARA", "WBD", "SPOT", "PINS"]

def fetch_engagement(row):
    t       = row["ticker"]
    traffic = client.web_traffic.traffic(t)
    return (t, {
        "ticker":        t,
        "duration":      traffic["visitDuration"],
        "pages_per":     traffic["pagesPerVisit"],
        "bounce_rate":   traffic["bounceRate"],
    })

df_tickers     = utils.df([{"ticker": t} for t in tickers])
engagement_map = {}
utils.work(df_tickers, fetch_engagement, engagement_map)

df_engagement = utils.df(list(engagement_map.values()))

visualize.scatter(
    df_engagement,
    x="bounce_rate",
    y="pages_per",
    hover=["ticker", "duration"],
)
`,
    },
    {
      label: 'Traffic vs. Revenue',
      language: 'python',
      filename: 'traffic_vs_revenue.py',
      bg: '3.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["AMZN", "SHOP", "EBAY", "ETSY", "CVNA", "CHWY"]

def fetch_combined(row):
    t       = row["ticker"]
    traffic = client.web_traffic.traffic(t)
    snap    = client.financials.snapshot(t)
    return (t, {
        "ticker":   t,
        "visitors": traffic["monthlyVisitors"],
        "revenue":  snap["revenue"],
        "fcf":      snap["freeCashFlow"],
    })

df_tickers   = utils.df([{"ticker": t} for t in tickers])
combined_map = {}
utils.work(df_tickers, fetch_combined, combined_map)

df_combined = utils.df(list(combined_map.values()))

visualize.fit(
    df_combined,
    x="visitors",
    y="revenue",
    hover=["ticker", "fcf"],
)
`,
    },
    {
      label: 'Global Reach Index',
      language: 'python',
      filename: 'global_reach_index.py',
      bg: '1.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["GOOGL", "META", "MSFT", "AMZN", "BIDU", "TCEHY"]

def fetch_reach(row):
    t       = row["ticker"]
    traffic = client.web_traffic.traffic(t)
    return (t, {
        "ticker":       t,
        "global_rank":  traffic["globalRank"],
        "country_rank": traffic["countryRank"],
        "top_countries": len(traffic["topCountries"]),
        "visitors":     traffic["monthlyVisitors"],
    })

df_tickers = utils.df([{"ticker": t} for t in tickers])
reach_map  = {}
utils.work(df_tickers, fetch_reach, reach_map)

df_reach = utils.df(list(reach_map.values()))
df_reach = df_reach.sort_values("global_rank")

visualize.scatter(
    df_reach,
    x="global_rank",
    y="top_countries",
    hover=["ticker", "visitors"],
)
`,
    },
  ]}
/>
      <Marquee />

      <FeaturesGrid
        title="Web Traffic Analytics. "
        subtitle="Website traffic data and consumer interest data API. "
        features={[
        {
          fig: 'FIG 0.1',
          icon: <Globe size={80} color="var(--purple)" />,
          title: 'Web Traffic Analytics',
          description: 'Unique visitors, page views, session duration, and bounce rates for 10,000+ corporate websites. Track web traffic data and demand signals at source.',
        },
        {
          fig: 'FIG 0.2',
          icon: <Smartphone size={80} color="var(--green)" />,
          title: 'App Store Intelligence',
          description: 'Daily download estimates, DAU/MAU, retention rates, and review sentiment across iOS and Android. Understand mobile engagement.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Search size={80} color="var(--red)" />,
          title: 'Search Trend Data',
          description: 'Brand and product keyword search volume. Identify rising consumer interest, competitive encroachment, and campaign effectiveness.',
        },
        {
          fig: 'FIG 0.4',
          icon: <Activity size={80} color="var(--purple)" />,
          title: 'User Engagement Metrics',
          description: 'Time spent, feature usage, and retention cohorts for digital products. See how users actually interact with apps and sites.',
        },
        {
          fig: 'FIG 0.5',
          icon: <BarChart3 size={80} color="var(--green)" />,
          title: 'Digital Market Share',
          description: 'Competitive positioning in online channels. Quantify market share by web traffic, app downloads, and search visibility.',
        },
        {
          fig: 'FIG 0.6',
          icon: <TrendingUp size={80} color="var(--red)" />,
          title: 'Revenue Prediction Signals',
          description: 'Correlate digital engagement with future revenue. Digital presence data that often leads traditional financial metrics by weeks.',
        },
      ]} />

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Alternative Digital Data"
        headingPlain="Web traffic data"
        headingEm="for 10,000+ websites."
        body="Comprehensive digital presence data across every sector - website traffic, mobile app engagement, and search trends, updated weekly."
        stats={[
          { v: "5,000+",  l: "Companies" },
          { v: "Weekly",  l: "Update Freq" },
          { v: "35yr",    l: "History" },
          { v: "99.95%",  l: "Uptime SLA" },
        ]}
        markers={[
          { id: "us",          location: [37.09, -95.71],   label: "United States" },
          { id: "uk",          location: [51.51, -0.09],    label: "United Kingdom" },
          { id: "germany",     location: [51.16, 10.45],    label: "Germany" },
          { id: "india",       location: [20.59, 78.96],    label: "India" },
          { id: "japan",       location: [35.68, 139.77],   label: "Japan" },

          { id: "canada",      location: [56.13, -106.35],  label: "Canada" },
          { id: "china",       location: [35.86, 104.19],   label: "China" },
          { id: "brazil",      location: [-14.23, -51.92],  label: "Brazil" },
          { id: "france",      location: [46.22, 2.21],     label: "France" },
          { id: "italy",       location: [41.87, 12.56],    label: "Italy" },

          { id: "south-korea", location: [37.56, 126.97],   label: "South Korea" },
          { id: "australia",   location: [-25.27, 133.77],  label: "Australia" },
          { id: "russia",      location: [61.52, 105.31],   label: "Russia" },
          { id: "mexico",      location: [23.63, -102.55],  label: "Mexico" },
          { id: "spain",       location: [40.46, -3.75],    label: "Spain" },

          { id: "netherlands", location: [52.13, 5.29],     label: "Netherlands" },
          { id: "sweden",      location: [60.12, 18.64],    label: "Sweden" },
          { id: "switzerland", location: [46.81, 8.22],     label: "Switzerland" },
          { id: "norway",      location: [60.47, 8.47],     label: "Norway" },
          { id: "denmark",     location: [56.26, 9.50],     label: "Denmark" },

          { id: "singapore",   location: [1.35, 103.82],    label: "Singapore" },
          { id: "indonesia",   location: [-0.78, 113.92],   label: "Indonesia" },
          { id: "malaysia",    location: [4.21, 101.97],    label: "Malaysia" },
          { id: "thailand",    location: [15.87, 100.99],   label: "Thailand" },
          { id: "vietnam",     location: [14.05, 108.28],   label: "Vietnam" },

          { id: "philippines", location: [12.88, 121.77],   label: "Philippines" },
          { id: "taiwan",      location: [23.69, 120.96],   label: "Taiwan" },
          { id: "hong-kong",   location: [22.32, 114.17],   label: "Hong Kong" },
          { id: "uae",         location: [23.42, 53.84],    label: "United Arab Emirates" },
          { id: "saudi",       location: [23.88, 45.08],    label: "Saudi Arabia" },

          { id: "turkey",      location: [38.96, 35.24],    label: "Turkey" },
          { id: "israel",      location: [31.05, 34.85],    label: "Israel" },
          { id: "south-africa",location: [-30.56, 22.94],   label: "South Africa" },
          { id: "egypt",       location: [26.82, 30.80],    label: "Egypt" },
          { id: "nigeria",     location: [9.08, 8.67],      label: "Nigeria" },

          { id: "argentina",   location: [-38.42, -63.62],  label: "Argentina" },
          { id: "chile",       location: [-35.67, -71.54],  label: "Chile" },
          { id: "colombia",    location: [4.57, -74.29],    label: "Colombia" },
          { id: "peru",        location: [-9.19, -75.01],   label: "Peru" },
          { id: "poland",      location: [51.92, 19.14],    label: "Poland" },

          { id: "belgium",     location: [50.50, 4.47],     label: "Belgium" },
          { id: "austria",     location: [47.52, 14.55],    label: "Austria" },
          { id: "portugal",    location: [39.40, -8.22],    label: "Portugal" },
          { id: "ireland",     location: [53.41, -8.24],    label: "Ireland" },
          { id: "finland",     location: [61.92, 25.74],    label: "Finland" },

          { id: "new-zealand", location: [-40.90, 174.88],  label: "New Zealand" },
          { id: "pakistan",    location: [30.37, 69.34],    label: "Pakistan" },
          { id: "bangladesh",  location: [23.68, 90.35],    label: "Bangladesh" },
          { id: "iran",        location: [32.43, 53.68],    label: "Iran" },
          { id: "ukraine",     location: [48.38, 31.16],    label: "Ukraine" },

          { id: "czechia",     location: [49.82, 15.47],    label: "Czech Republic" },
          { id: "romania",     location: [45.94, 24.96],    label: "Romania" },
          { id: "greece",      location: [39.07, 21.82],    label: "Greece" },
          { id: "morocco",     location: [31.79, -7.09],    label: "Morocco" },
          { id: "kenya",       location: [-0.02, 37.90],    label: "Kenya" }
        ]}
      />

      <DataGrid
        title="Web Traffic Data API: The Digital Pulse of Business"
        subtitle="See the demand signal before the earnings report. Track current and historical website visits, app downloads, search trends, and geographic traffic to quantify digital engagement, consumer interest, and brand momentum."
        bg={databg}
        stats={[
          { label: 'Web Domains Tracked', value: '1M+' },
          { label: 'Traffic Metrics', value: '50+' },
          { label: 'Data Latency', value: '< 24 hours' },
        ]}
      />
      <CardList
        title="From Clicks to Insights with Web Traffic Data"
        vid={'https://cdn.studio14a.com/axion/products/cards/wt.mp4'}
        points={[
          {
            "title": "Global Traffic & Engagement Metrics",
            "details": "Monitor estimated unique visitors, page views, bounce rates, visit duration, and traffic sources (direct, search, social) for any public company's digital properties with web traffic data.",
            "cta": "Explore traffic metrics",
            "url": "/docs"
          },
          {
            "title": "App Store Intelligence & Rankings",
            "details": "Track daily mobile app download estimates, revenue projections, category rankings, and user review sentiment across iOS and Android platforms globally.",
            "cta": "View app analytics",
            "url": "/products/sentiment"
          },
          {
            "title": "Search Trend & Keyword Demand",
            "details": "Analyze search volume for brand and product-related keywords. Identify rising consumer interest, competitive encroachment, and marketing campaign effectiveness.",
            "cta": "Analyze search trends",
            "url": "/docs"
          }
        ]}
      />

      <Responses
        heading='Single source of digital truth'
        subheading='A system of record for the alternative data that powers your investment process'
        ticker='AAPL'
        tabs={[
          {
            title: 'Traffic Overview',
            description: 'Key web engagement metrics for corporate websites',
            data: {
              metrics: [
                { name: 'Unique Visitors (Monthly)', value: '142M', change: '+12%' },
                { name: 'Page Views', value: '890M', change: '+8%' },
                { name: 'Avg. Session Duration', value: '4m 23s', change: '-3%' },
                { name: 'Bounce Rate', value: '42%', change: '-5pp' },
              ]
            },
          },
          {
            title: 'Geography',
            description: 'Traffic distribution by country',
            data: {
              regions: [
                { country: 'United States', share: '42%' },
                { country: 'China', share: '18%' },
                { country: 'United Kingdom', share: '9%' },
                { country: 'Germany', share: '7%' },
                { country: 'Japan', share: '6%' },
              ]
            },
          },
          {
            title: 'Traffic Sources',
            description: 'Direct, search, social, and referral breakdown',
            data: {
              sources: [
                { channel: 'Direct', share: '35%' },
                { channel: 'Organic Search', share: '30%' },
                { channel: 'Social', share: '20%' },
                { channel: 'Referral', share: '10%' },
                { channel: 'Paid Search', share: '5%' },
              ]
            },
          },
          {
            title: 'App Downloads',
            description: 'Daily download estimates and DAU/MAU',
            data: {
              app: [
                { metric: 'Daily Downloads (iOS)', value: '48,000' },
                { metric: 'Daily Downloads (Android)', value: '62,000' },
                { metric: 'DAU', value: '12.4M' },
                { metric: 'MAU', value: '34.7M' },
                { metric: 'Retention (D30)', value: '42%' },
              ]
            },
          },
          {
            title: 'Search Keywords',
            description: 'Top brand and product search terms',
            data: {
              keywords: [
                { term: 'iPhone', volume: '2.4M/mo', trend: '↑' },
                { term: 'Apple', volume: '18.2M/mo', trend: '→' },
                { term: 'iPad', volume: '1.1M/mo', trend: '↓' },
                { term: 'MacBook', volume: '980K/mo', trend: '↑' },
              ]
            },
          },
          {
            title: 'Engagement',
            description: 'Feature usage, time spent, and cohort analysis',
            data: {
              engagement: [
                { feature: 'Shopping', time: '8m 12s', freq: '3.2/week' },
                { feature: 'Support', time: '4m 55s', freq: '0.8/week' },
                { feature: 'Community', time: '6m 30s', freq: '2.1/week' },
              ]
            },
          },
        ]}
      />

      <DatasetDetails full={true}
        title="Comprehensive Web Traffic Data Coverage"
        subtitle="Access weekly website visitor analytics, mobile app download estimates, user engagement metrics, and digital market share for over 5,000 public companies through our web traffic data API. Spot revenue trends weeks before official disclosures with alternative data that powers quant models, screening, and backtesting."
        cta="Explore Data"
        ctaLink="/login"
        tiles={[
          {
            img: '0.webp',
            title: 'We track 5,000+ companies',
            caption: 'You can monitor digital engagement and web traffic for thousands of public companies.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '3.webp',
            title: 'We monitor 10,000+ websites',
            caption: 'You get weekly visitor analytics and engagement metrics for corporate websites across all sectors.',
            cn: 'scaled corner',
            tile: <W2/>
          },
          {
            img: '1.webp',
            title: 'We analyze 50,000+ mobile apps',
            caption: 'You can estimate app downloads and user engagement for publicly traded companies.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '6.webp',
            title: 'We maintain 35+ years of history',
            caption: 'You can backtest digital signals against revenue trends across decades of data.',
            cn: 'scaled overthetop',
            tile: <W4/>
          },
          {
            img: '4.webp',
            title: 'We refresh weekly',
            caption: 'You receive updated engagement metrics every week for timely investment insights.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Numbers that speak for themselves."
        leftCell={{ stat: '89%', title: '89% forecast accuracy lift.', desc: 'When combining web traffic with fundamentals.', showChart: true }}
        rightCells={[
          { stat: '5,000+', title: 'Companies Tracked.', desc: 'Every major public company globally.' },
          { stat: 'Weekly', title: 'Fresh Data Every Week.', desc: 'Timely updates for earnings models.', boldDesc: true },
        ]}
      />

      <ProductFAQ slug="webtraffic" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default WebTrafficPage;
